import {
  Quality,
  DEGREE_TO_SEMITONE_MAJOR,
  DEGREE_TO_SEMITONE_NAT_MINOR,
  DEGREE_TO_SEMITONE_HARM_MINOR,
  QUALITY_TO_OFFSETS,
} from "./romanParser";
import {
  noteNameToMidi,
  ALL_ROOTS,
  ALL_EXTENSIONS,
  ChordExtension,
} from "./chordUtils";

// Extension offsets (same as chordToMidi logic)
const MAJOR_7TH_OFFSET = 11;
const MINOR_7TH_OFFSET = 10;
const SIXTH_OFFSET = 9;
const NINTH_OFFSET = 14; // 2 + 12

// Re-export ChordExtension for convenience
export type { ChordExtension };

export interface ChordSuggestion {
  rootColor: "green" | "yellow";
  extensionColor: "green" | "yellow" | "red";
}

export type ChordGrid = Record<string, Record<string, ChordSuggestion>>;

/**
 * Get all scale notes as MIDI numbers for a given key and mode
 */
export function getScaleNotes(
  key: string,
  mode: "major" | "naturalMinor" | "harmonicMinor"
): number[] {
  const keyRootMidi = noteNameToMidi(key, 4);
  let scaleSemitones: number[];
  if (mode === "major") {
    scaleSemitones = DEGREE_TO_SEMITONE_MAJOR;
  } else if (mode === "naturalMinor") {
    scaleSemitones = DEGREE_TO_SEMITONE_NAT_MINOR;
  } else {
    scaleSemitones = DEGREE_TO_SEMITONE_HARM_MINOR;
  }

  return scaleSemitones.map((semitones) => keyRootMidi + semitones);
}

/**
 * Build chord MIDI notes from absolute root and extension name
 * Uses the same logic as chordToMidi but from absolute root
 */
export function buildChordFromRootAndExtension(
  rootMidi: number,
  extension: string
): number[] {
  const chord: number[] = [];

  // Map extension to quality and additional offsets
  let baseOffsets: number[] = [];
  let hasSeventh = false;
  let seventhOffset: number | null = null;
  const extraOffsets: number[] = [];

  switch (extension) {
    case "maj":
      baseOffsets = QUALITY_TO_OFFSETS[Quality.MAJ];
      break;
    case "min":
      baseOffsets = QUALITY_TO_OFFSETS[Quality.MIN];
      break;
    case "dim":
      baseOffsets = QUALITY_TO_OFFSETS[Quality.DIM];
      break;
    case "aug":
      baseOffsets = QUALITY_TO_OFFSETS[Quality.AUG];
      break;
    case "sus2":
      baseOffsets = QUALITY_TO_OFFSETS[Quality.SUS2];
      break;
    case "sus4":
      baseOffsets = QUALITY_TO_OFFSETS[Quality.SUS4];
      break;
    case "5":
      baseOffsets = QUALITY_TO_OFFSETS[Quality.POWER5];
      break;
    case "maj7":
      baseOffsets = QUALITY_TO_OFFSETS[Quality.MAJ];
      hasSeventh = true;
      seventhOffset = MAJOR_7TH_OFFSET;
      break;
    case "7":
      baseOffsets = QUALITY_TO_OFFSETS[Quality.MAJ];
      hasSeventh = true;
      seventhOffset = MINOR_7TH_OFFSET;
      break;
    case "9":
      baseOffsets = QUALITY_TO_OFFSETS[Quality.MAJ];
      hasSeventh = true;
      seventhOffset = MINOR_7TH_OFFSET;
      extraOffsets.push(NINTH_OFFSET);
      break;
    case "min7":
      baseOffsets = QUALITY_TO_OFFSETS[Quality.MIN];
      hasSeventh = true;
      seventhOffset = MINOR_7TH_OFFSET;
      break;
    case "min9":
      baseOffsets = QUALITY_TO_OFFSETS[Quality.MIN];
      hasSeventh = true;
      seventhOffset = MINOR_7TH_OFFSET;
      extraOffsets.push(NINTH_OFFSET);
      break;
    case "6":
      // Major 6th chord
      baseOffsets = QUALITY_TO_OFFSETS[Quality.MAJ];
      extraOffsets.push(SIXTH_OFFSET);
      break;
    case "min6":
      baseOffsets = QUALITY_TO_OFFSETS[Quality.MIN];
      extraOffsets.push(SIXTH_OFFSET);
      break;
    case "add6":
      // Major add6 chord
      baseOffsets = QUALITY_TO_OFFSETS[Quality.MAJ];
      extraOffsets.push(SIXTH_OFFSET);
      break;
    case "add9":
      // Major add9 chord (no 7th)
      baseOffsets = QUALITY_TO_OFFSETS[Quality.MAJ];
      extraOffsets.push(NINTH_OFFSET);
      break;
    default:
      // Default to major
      baseOffsets = QUALITY_TO_OFFSETS[Quality.MAJ];
  }

  // Build chord from base offsets
  chord.push(...baseOffsets.map((offset) => rootMidi + offset));

  // Add 7th if present
  if (hasSeventh && seventhOffset !== null) {
    chord.push(rootMidi + seventhOffset);
  }

  // Add extra offsets (6th, 9th, etc.)
  if (extraOffsets.length > 0) {
    chord.push(...extraOffsets.map((offset) => rootMidi + offset));
  }

  // Sort and return
  return chord.sort((a, b) => a - b);
}

/**
 * Determines if root is diatonic (green) or chromatic (yellow)
 */
export function getRootColor(
  rootMidi: number,
  scaleNotes: number[]
): "green" | "yellow" {
  const rootPitchClass = rootMidi % 12;
  const scalePitchClasses = scaleNotes.map((note) => note % 12);

  return scalePitchClasses.includes(rootPitchClass) ? "green" : "yellow";
}

/**
 * Determines extension color based on non-scale tones (excluding root)
 * 0 non-scale tones = green
 * 1 non-scale tone = yellow
 * 2+ non-scale tones = red
 */
export function getExtensionColor(
  chordNotes: number[],
  rootMidi: number,
  scaleNotes: number[]
): "green" | "yellow" | "red" {
  const rootPitchClass = rootMidi % 12;

  // Get all chord notes excluding root (by pitch class, not just octave)
  const extensionNotes = chordNotes.filter(
    (note) => note % 12 !== rootPitchClass
  );

  // Convert to pitch classes (mod 12)
  const extensionPitchClasses = extensionNotes.map((note) => note % 12);
  const scalePitchClasses = scaleNotes.map((note) => note % 12);

  // Count non-scale tones
  let nonScaleToneCount = 0;
  const seenPitchClasses = new Set<number>();

  for (const pitchClass of extensionPitchClasses) {
    if (!seenPitchClasses.has(pitchClass)) {
      seenPitchClasses.add(pitchClass);
      if (!scalePitchClasses.includes(pitchClass)) {
        nonScaleToneCount++;
      }
    }
  }

  // Map count to color
  if (nonScaleToneCount === 0) {
    return "green";
  } else if (nonScaleToneCount === 1) {
    return "yellow";
  } else {
    return "red";
  }
}

/**
 * Main function that returns both root and extension colors for a chord
 */
export function getChordSuggestion(
  rootNote: string,
  extension: string,
  key: string,
  mode: "major" | "naturalMinor" | "harmonicMinor"
): ChordSuggestion {
  // Convert root note to MIDI
  const rootMidi = noteNameToMidi(rootNote, 4);

  // Build chord
  const chordNotes = buildChordFromRootAndExtension(rootMidi, extension);

  // Get scale notes
  const scaleNotes = getScaleNotes(key, mode);

  // Calculate colors
  const rootColor = getRootColor(rootMidi, scaleNotes);
  const extensionColor = getExtensionColor(chordNotes, rootMidi, scaleNotes);

  return {
    rootColor,
    extensionColor,
  };
}

/**
 * Generates full grid data for all 12 roots and all extensions
 */
export function generateChordGrid(
  key: string,
  mode: "major" | "naturalMinor" | "harmonicMinor"
): ChordGrid {
  const grid: ChordGrid = {};

  for (const root of ALL_ROOTS) {
    grid[root] = {};
    for (const extension of ALL_EXTENSIONS) {
      grid[root][extension] = getChordSuggestion(root, extension, key, mode);
    }
  }

  return grid;
}
